import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContentKpiCards } from '@/components/content/content-kpi-cards';
import { ContentTimeseriesChart } from '@/components/content/content-timeseries-chart';
import { ContentDropoffChart } from '@/components/content/content-dropoff-chart';
import {
  useContentDetail,
  useContentDropoff,
  useContentMetrics,
  useContentTimeseries,
} from '@/hooks/use-content';

// SPEC-021: ContentMetricsPage — per-item analytics. Teachers see only
// their own content (server-enforced); admins see everything.

type Range = '7d' | '30d' | '90d';

export default function ContentMetricsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>('30d');

  const detail = useContentDetail(id);
  const metrics = useContentMetrics(id);
  const series = useContentTimeseries(id, range);
  const dropoff = useContentDropoff(id);

  const item = detail.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Button>
        <div>
          <h1 className="text-xl font-semibold">
            {item?.title ?? 'Content metrics'}
          </h1>
          {item?.published_at && (
            <p className="text-xs text-muted-foreground">
              Published {new Date(item.published_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {metrics.isLoading ? (
        <LoadingBlock label="Loading metrics…" />
      ) : metrics.data ? (
        <ContentKpiCards kpis={metrics.data} />
      ) : (
        <EmptyBlock message="No metrics available yet." />
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">Daily unique viewers</CardTitle>
          <Select value={range} onValueChange={(v) => setRange((v as Range) ?? '30d')}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {series.isLoading ? (
            <LoadingBlock label="Loading time series…" />
          ) : series.data && series.data.length > 0 ? (
            <ContentTimeseriesChart data={series.data} />
          ) : (
            <EmptyBlock message="No view events in this range." />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Reader drop-off</CardTitle>
          <p className="text-xs text-muted-foreground">
            Distinct users who reached each decile of progress.
          </p>
        </CardHeader>
        <CardContent>
          {dropoff.isLoading ? (
            <LoadingBlock label="Loading drop-off…" />
          ) : dropoff.data && dropoff.data.length > 0 ? (
            <ContentDropoffChart data={dropoff.data} />
          ) : (
            <EmptyBlock message="No progress events yet." />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LoadingBlock({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  );
}

function EmptyBlock({ message }: { message: string }) {
  return (
    <div className="p-8 text-center text-sm text-muted-foreground">{message}</div>
  );
}
